/* eslint-disable react/prop-types */
import { OpenEndedAnswerType } from '@sm/question-widgets/respondent-survey';
import React, { createContext, useState, useCallback, useMemo, useContext, FC } from 'react';

import { GetCurrentSurveySessionQuery } from '~lib/generatedGqlTypes';
import { SurveyContext } from './SurveyContext';
import { QuestionAnswers } from './types';

type CurrentPageContextType = {
  questions?: NonNullable<
    NonNullable<GetCurrentSurveySessionQuery['surveyTakingSession']['currentPage']>['questionsQM']
  >['items'];
  answers?: QuestionAnswers;
  totalAnswers: number;
  updateAnswer(answer: OpenEndedAnswerType): void;
};

export const CurrentPageContext = createContext<CurrentPageContextType>({
  questions: undefined,
  answers: undefined,
  totalAnswers: 0,
  updateAnswer: (answer: OpenEndedAnswerType) => {},
});

type CurrentPageProviderProps = {
  children: React.ReactNode;
};

export const CurrentPageProvider: FC<CurrentPageProviderProps> = ({ children }) => {
  // Local storage -> overrides data from survey.
  // Local storage should be cleared when we hit the next page button.

  const { activePage } = useContext(SurveyContext);
  const [questions] = useState(activePage?.questionsQM?.items);

  const initialAnswers = questions?.reduce(
    (acc, curr) => ({ ...acc, [curr.id]: { questionId: curr.id, value: '', touched: false, isDirty: false } }),
    {}
  );

  // Hydrate answers
  const [answers, setAnswers] = useState<QuestionAnswers | undefined>(initialAnswers);

  const updateAnswer = useCallback(
    answer => {
      setAnswers({
        ...answers,
        [answer.questionId]: answer,
      });
    },
    [answers]
  );

  /**
   * the total number of questions that have a value (i.e. answered), on the current page only
   * TODO: refactor to include answered questions on other pages once we get that data from the backend
   *  */
  const totalAnswers = useMemo(
    () => (!answers ? 0 : Object.values(answers).reduce((acc: number, answer) => (answer.value ? acc + 1 : acc), 0)),
    [answers]
  );

  return (
    <CurrentPageContext.Provider
      value={{
        questions,
        answers,
        totalAnswers,
        updateAnswer,
      }}
    >
      <>{children}</>
    </CurrentPageContext.Provider>
  );
};

export default CurrentPageProvider;
